import { useEffect, useState, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import FormField, { Input, Select, Textarea } from "../components/ui/FormField";
import styles from "./Jobs.module.css";

const SERVICE_TYPES = [
  "Pay While Working - Europe",
  "Gulf Job Opportunities",
  "Canada Visa Application",
];

const EMPTY = {
  title: "", service_type: "", country: "",
  description: "", requirements: "", deadline: "",
  is_active: true, poster_url: "",
};

export default function Jobs() {
  const { isAdmin } = useAuth();
  const [jobs,        setJobs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [form,        setForm]        = useState(EMPTY);
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState("");
  const [posterFile,  setPosterFile]  = useState(null);   // File object
  const [posterPreview, setPosterPreview] = useState(""); // local blob URL
  const [uploading,   setUploading]   = useState(false);
  const [preview,     setPreview]     = useState(null);   // job being previewed
  const fileRef = useRef(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("jobs")
      .select("*")
      .order("created_at", { ascending: false });
    setJobs(data ?? []);
    setLoading(false);
  }

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setPosterFile(null);
    setPosterPreview("");
    setErr("");
    setModal(true);
  }

  function openEdit(job) {
    setEditing(job);
    setForm({ ...EMPTY, ...job });
    setPosterFile(null);
    setPosterPreview(job.poster_url ?? "");
    setErr("");
    setModal(true);
  }

  function set(field, val) {
    setForm((f) => ({ ...f, [field]: val }));
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      setErr("Please upload a JPG, PNG, WEBP or GIF image.");
      return;
    }

    // Validate size — max 5MB
    if (file.size > 5 * 1024 * 1024) {
      setErr("Image must be under 5MB.");
      return;
    }

    setErr("");
    setPosterFile(file);
    setPosterPreview(URL.createObjectURL(file));
  }

  function removePoster() {
    setPosterFile(null);
    setPosterPreview("");
    set("poster_url", "");
    if (fileRef.current) fileRef.current.value = "";
  }

  async function uploadPoster(file) {
    const ext      = file.name.split(".").pop();
    const filename = `poster-${Date.now()}.${ext}`;
    const path     = `posters/${filename}`;

    const { error } = await supabase.storage
      .from("job-posters")
      .upload(path, file, { upsert: true, contentType: file.type });

    if (error) throw new Error(error.message);

    const { data } = supabase.storage
      .from("job-posters")
      .getPublicUrl(path);

    return data.publicUrl;
  }

  async function handleSave() {
    if (!form.title.trim() || !form.service_type) {
      setErr("Title and service type are required.");
      return;
    }

    setSaving(true);
    setErr("");

    try {
      let posterUrl = form.poster_url ?? "";

      // Upload new poster if one was selected
      if (posterFile) {
        setUploading(true);
        posterUrl = await uploadPoster(posterFile);
        setUploading(false);
      }

      // If poster was removed
      if (!posterPreview && !posterFile) posterUrl = null;

      const payload = {
        title:        form.title.trim(),
        service_type: form.service_type,
        country:      form.country      || null,
        description:  form.description  || null,
        requirements: form.requirements || null,
        deadline:     form.deadline     || null,
        is_active:    form.is_active,
        poster_url:   posterUrl         || null,
      };

      let error;
      if (editing) {
        ({ error } = await supabase.from("jobs").update(payload).eq("id", editing.id));
      } else {
        ({ error } = await supabase.from("jobs").insert(payload));
      }

      if (error) throw new Error(error.message);

      setModal(false);
      load();
    } catch (e) {
      setErr(e.message);
      setUploading(false);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(job) {
    await supabase.from("jobs").update({ is_active: !job.is_active }).eq("id", job.id);
    setJobs((prev) => prev.map((j) => j.id === job.id ? { ...j, is_active: !j.is_active } : j));
  }

  async function deleteJob(id) {
    if (!window.confirm("Delete this job listing?")) return;
    await supabase.from("jobs").delete().eq("id", id);
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }

  return (
    <div className={styles.page}>
      {isAdmin && (
        <div className={styles.toolbar}>
          <span className={styles.count}>
            {jobs.length} listing{jobs.length !== 1 ? "s" : ""}
          </span>
          <button className={styles.addBtn} onClick={openNew}>
            + Add Job Listing
          </button>
        </div>
      )}

      {loading ? (
        <div className={styles.empty}>Loading…</div>
      ) : jobs.length === 0 ? (
        <div className={styles.empty}>No job listings yet.</div>
      ) : (
        <div className={styles.grid}>
          {jobs.map((job) => (
            <div
              key={job.id}
              className={`${styles.card} ${!job.is_active ? styles.inactive : ""}`}
            >
              {/* Poster image */}
              {job.poster_url && (
                <div className={styles.posterWrap}>
                  <img
                    src={job.poster_url}
                    alt={`${job.title} poster`}
                    className={styles.posterImg}
                    onClick={() => setPreview(job)}
                  />
                  <button
                    className={styles.posterExpand}
                    onClick={() => setPreview(job)}
                    title="View full poster"
                  >
                    ⤢ View Poster
                  </button>
                </div>
              )}

              <div className={styles.cardTop}>
                <div>
                  <div className={styles.jobTitle}>{job.title}</div>
                  <div className={styles.jobService}>{job.service_type}</div>
                  {job.country && (
                    <div className={styles.jobCountry}>📍 {job.country}</div>
                  )}
                </div>
                <Badge label={String(job.is_active)} />
              </div>

              {job.description && (
                <p className={styles.jobDesc}>{job.description}</p>
              )}

              {job.requirements && (
                <div className={styles.jobReqs}>
                  <strong>Requirements:</strong> {job.requirements}
                </div>
              )}

              <div className={styles.cardFooter}>
                {job.deadline && (
                  <span className={styles.deadline}>
                    Deadline: {job.deadline}
                  </span>
                )}
                {isAdmin && (
                  <div className={styles.actions}>
                    <button className={styles.editBtn} onClick={() => openEdit(job)}>
                      Edit
                    </button>
                    <button className={styles.toggleBtn} onClick={() => toggleActive(job)}>
                      {job.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button className={styles.delBtn} onClick={() => deleteJob(job.id)}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {modal && (
        <Modal
          title={editing ? "Edit Job Listing" : "Add Job Listing"}
          onClose={() => setModal(false)}
          width={620}
        >
          <div className={styles.modalForm}>
            <FormField label="Job Title" required>
              <Input
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="e.g. Warehouse Operative"
              />
            </FormField>

            <div className={styles.grid2}>
              <FormField label="Service Type" required>
                <Select
                  value={form.service_type}
                  onChange={(e) => set("service_type", e.target.value)}
                >
                  <option value="" disabled>Select…</option>
                  {SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}
                </Select>
              </FormField>
              <FormField label="Country / Location">
                <Input
                  value={form.country}
                  onChange={(e) => set("country", e.target.value)}
                  placeholder="e.g. Ireland"
                />
              </FormField>
            </div>

            <FormField label="Description">
              <Textarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Job overview…"
              />
            </FormField>

            <FormField label="Requirements">
              <Textarea
                value={form.requirements}
                onChange={(e) => set("requirements", e.target.value)}
                placeholder="Qualifications, experience…"
              />
            </FormField>

            <div className={styles.grid2}>
              <FormField label="Application Deadline">
                <Input
                  type="date"
                  value={form.deadline}
                  onChange={(e) => set("deadline", e.target.value)}
                />
              </FormField>
              <FormField label="Status">
                <Select
                  value={String(form.is_active)}
                  onChange={(e) => set("is_active", e.target.value === "true")}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </FormField>
            </div>

            {/* ── Poster Upload ── */}
            <FormField label="Job Poster (JPG, PNG, WEBP — max 5MB)">
              {posterPreview ? (
                <div className={styles.previewWrap}>
                  <img
                    src={posterPreview}
                    alt="Poster preview"
                    className={styles.previewImg}
                  />
                  <div className={styles.previewActions}>
                    <button
                      type="button"
                      className={styles.changeBtn}
                      onClick={() => fileRef.current?.click()}
                    >
                      Change Poster
                    </button>
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={removePoster}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={styles.uploadZone}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleFileChange({ target: { files: [file] } });
                  }}
                >
                  <div className={styles.uploadIcon}>🖼</div>
                  <div className={styles.uploadText}>
                    Click or drag &amp; drop to upload poster
                  </div>
                  <div className={styles.uploadSub}>JPG, PNG, WEBP up to 5MB</div>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </FormField>

            {err && <div className={styles.err}>{err}</div>}

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => setModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving || uploading}
              >
                {uploading ? "Uploading poster…" : saving ? "Saving…" : editing ? "Save Changes" : "Add Listing"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Full poster lightbox ── */}
      {preview && (
        <div className={styles.lightbox} onClick={() => setPreview(null)}>
          <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
            <button className={styles.lightboxClose} onClick={() => setPreview(null)}>✕</button>
            <img
              src={preview.poster_url}
              alt={preview.title}
              className={styles.lightboxImg}
            />
            <div className={styles.lightboxCaption}>
              <strong>{preview.title}</strong>
              {preview.country && <span> · {preview.country}</span>}
              {preview.deadline && <span> · Deadline: {preview.deadline}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}